import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import packageJson from '../../../../package.json';

type VersionManifest = {
  version: string;
  buildTimestamp?: string;
};

@Injectable({
  providedIn: 'root',
})
export class VersionCheckService {
  private http = inject(HttpClient);
  private readonly manifestUrl = 'assets/app-version.json';
  private readonly currentVersion = packageJson.version ?? '0.0.0';
  private readonly currentBuildTimestamp = packageJson.buildTimestamp ?? '';
  private readonly checkIntervalMs = 5 * 60 * 1000;
  private readonly requestTimeoutMs = 5000;
  private readonly isBrowser = typeof window !== 'undefined';
  private readonly hasNavigator = typeof navigator !== 'undefined';
  private intervalId?: ReturnType<typeof setInterval>;
  private isReloadInProgress = false;

  initialize(): Promise<void> {
    if (!this.isBrowser) {
      return Promise.resolve();
    }

    if (!this.intervalId) {
      this.intervalId = setInterval(() => {
        void this.checkForUpdate();
      }, this.checkIntervalMs);
    }

    return this.checkForUpdate();
  }

  private async checkForUpdate(): Promise<void> {
    try {
      if (!this.canCheckNow()) {
        return;
      }

      const manifest = await this.fetchRemoteManifest();

      if (!manifest) {
        return;
      }

      if (this.isOutdated(manifest)) {
        await this.reloadWithFreshAssets(manifest);
      }
    } catch (error) {
      console.warn('[VersionCheckService] Unable to verify application version', error);
    }
  }

  private async fetchRemoteManifest(): Promise<VersionManifest | null> {
    const headers = new HttpHeaders({
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    });

    const params = new HttpParams({
      fromObject: {
        'ngsw-bypass': 'true',
        _: Date.now().toString(),
      },
    });

    try {
      return await firstValueFrom(
        this.http.get<VersionManifest>(this.manifestUrl, { headers, params }).pipe(timeout(this.requestTimeoutMs))
      );
    } catch (error) {
      console.warn('[VersionCheckService] Manifest request failed', error);
      return null;
    }
  }

  private canCheckNow(): boolean {
    if (!this.isBrowser) {
      return false;
    }

    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return false;
    }

    if (this.hasNavigator && 'onLine' in navigator && !navigator.onLine) {
      return false;
    }

    return true;
  }

  private isOutdated(manifest: VersionManifest): boolean {
    const nextVersion = manifest.version ?? '';
    const nextBuildTimestamp = manifest.buildTimestamp ?? '';

    return (
      (nextVersion !== '' && nextVersion !== this.currentVersion) ||
      (nextBuildTimestamp !== '' && nextBuildTimestamp !== this.currentBuildTimestamp)
    );
  }

  private async reloadWithFreshAssets(manifest: VersionManifest): Promise<void> {
    if (this.isReloadInProgress) {
      return;
    }

    this.isReloadInProgress = true;

    try {
      console.info('[VersionCheckService] Detected newer application assets', {
        currentVersion: this.currentVersion,
        nextVersion: manifest.version ?? 'unknown',
        currentBuildTimestamp: this.currentBuildTimestamp || 'n/a',
        nextBuildTimestamp: manifest.buildTimestamp ?? 'unknown',
      });

      await this.flushCaches();
      await this.unregisterServiceWorkers();
      window.location.reload();
    } catch (error) {
      this.isReloadInProgress = false;
      console.warn('[VersionCheckService] Failed to apply version update', error);
    }
  }

  private async unregisterServiceWorkers(): Promise<void> {
    if (!this.hasNavigator || !('serviceWorker' in navigator)) {
      return;
    }

    const registrations = await navigator.serviceWorker.getRegistrations();

    for (const registration of registrations) {
      if (!registration) {
        continue;
      }

      try {
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }

        await registration.unregister();
      } catch (error) {
        console.warn(
          `[VersionCheckService] Failed to unregister service worker for scope ${registration.scope}`,
          error
        );
      }
    }
  }

  private async flushCaches(): Promise<void> {
    if (typeof caches === 'undefined') {
      return;
    }

    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
  }

  destroy(): void {
    if (!this.intervalId) {
      return;
    }

    clearInterval(this.intervalId);
    this.intervalId = undefined;
  }
}
